import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { FaChartLine, FaFilter } from "react-icons/fa";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function RevenueGrowth() {
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ from: "", to: "" });
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);

  const vendorId = localStorage.getItem("vendorId");

  const fetchRevenueGrowth = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${baseurl}getVendorRevenueGrowth/${vendorId}?from_date=${filters.from}&to_date=${filters.to}`
      );
      if (res.data.success) {
        const data = res.data.monthlyData.map((d) => ({
          month: d.month,
          revenue: Number(d.revenue),
          quantity_sold: Number(d.quantity_sold),
        }));

        setMonthlyData(data);
        setTotalRevenue(res.data.totalRevenue || 0);
        setTotalQuantity(res.data.totalQuantity || 0);
      } else {
        setMonthlyData([]);
      }
    } catch (err) {
      console.error(err);
      setMonthlyData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vendorId) fetchRevenueGrowth();
  }, [vendorId]);

  // Prepare chart data
  const chartData = {
    labels: monthlyData.map((d) => d.month),
    datasets: [
      {
        label: "Revenue (₹)",
        data: monthlyData.map((d) => d.revenue),
        borderColor: "#f54c4d",
        backgroundColor: "rgba(245, 76, 77, 0.2)",
        fill: true,
        tension: 0.3,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
      {
        label: "Quantity Sold",
        data: monthlyData.map((d) => d.quantity_sold),
        borderColor: "#36a2eb",
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        fill: true,
        tension: 0.3,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Revenue Growth Over Months" },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: function (tooltipItem) {
            return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
          },
        },
      },
    },
    interaction: { mode: "nearest", axis: "x", intersect: false },
    scales: {
      y: { beginAtZero: true },
      x: {
        title: { display: true, text: "Month" },
      },
    },
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="main-content">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="breadcrumb-title d-flex align-items-center gap-2">
              <FaChartLine /> Revenue Growth
            </h4>
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter /> Filters
            </button>
          </div>

          {/* Filters */}
          {showFilter && (
            <div className="card p-3 mb-3 shadow-sm border-0">
              <div className="row g-2 align-items-end">
                <div className="col-md-3">
                  <label>From</label>
                  <input
                    type="month"
                    className="form-control"
                    value={filters.from}
                    onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label>To</label>
                  <input
                    type="month"
                    className="form-control"
                    value={filters.to}
                    onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                  />
                </div>
                <div className="col-md-2">
                  <button className="btn btn-primary w-100" onClick={fetchRevenueGrowth}>
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Summary Cards */}
          <div className="d-flex gap-3 mb-3 flex-wrap">
            <div className="card p-3 flex-fill text-center shadow-sm border-0 bg-gradient-light">
              <h6>Total Revenue</h6>
              <h4>₹{totalRevenue}</h4>
            </div>
            <div className="card p-3 flex-fill text-center shadow-sm border-0 bg-gradient-light-blue">
              <h6>Total Quantity Sold</h6>
              <h4>{totalQuantity}</h4>
            </div>
          </div>

          {/* Chart */}
          <div className="card shadow-sm border-0 p-3">
            {loading ? (
              <div className="text-center py-4 text-muted">Loading revenue growth...</div>
            ) : monthlyData.length > 0 ? (
              <Line data={chartData} options={chartOptions} />
            ) : (
              <div className="text-center py-4 text-muted">No data available</div>
            )}
          </div>
        </div>
      </main>

      <style jsx>{`
        .card h6 {
          color: #555;
          font-weight: 500;
        }
        .card h4 {
          font-weight: 700;
          margin-top: 5px;
        }
        .bg-gradient-light {
          background: linear-gradient(90deg, #ffe6e6, #fdd8d8);
        }
        .bg-gradient-light-blue {
          background: linear-gradient(90deg, #e6f0ff, #cce0ff);
        }
      `}</style>
    </>
  );
}

export default RevenueGrowth;
